'use client';

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { casesListQueryOptions, useCasesList } from '@/hooks/useCasesList';
import { useProvenanceSession } from '@/hooks/useProvenanceSession';
import { usePersonalFilesCaseId } from '@/hooks/usePersonalFilesCaseId';
import { useReactiveAuthTokens, useReactiveFeatureFlagValue } from '@/app/contexts';
import { applyAdHocCaseListPolicy } from '@/shared/ad-hoc-evidence';
import { getTenantIdFromToken } from '@/shared/csm-utils';
import type { CaseSummary } from '@/shared/cors-provenance-api';
import {
  caseFolderLabels,
  isAllCasesSelection,
  resolveCaseFolderName,
} from '@/shared/case-summary';

/**
 * The S3 FOLDER NAME of the currently selected case — its ``folder_token`` (a
 * UUID) when it has one, else its display name — for sending to
 * assistant-service as ``caseFolderName``. permission-service calls the same
 * value ``folder_segment`` (``derive_folder_segment``).
 *
 * The folder name is fixed when the case is created and never moves; the display
 * name is mutable (a UI rename or a SharePoint/Drive source-folder rename), which
 * is why every per-case key derives from this and not from the name.
 *
 * Why this exists: ``selectedCase`` on a chat request is the *display name*, and
 * assistant-service derived its retrieval filter from it as
 * ``case:<name.toLowerCase()>``. That is only correct for legacy name-folder
 * cases. A case minted with a ``folder_token`` has its documents tagged
 * ``case:<uuid>``, so the derived filter matched nothing and every question about
 * such a case failed with ``ZeroRetrievalResultsError`` — for every user, in every
 * tenant, whether or not case-access enforcement was on.
 *
 * The bare folder name is sent rather than the finished ``case:<folderName>`` tag
 * because the server needs it in two casings and lowercasing is not reversible:
 *
 *   - ``case:<folderName.lower()>`` for the retrieval filter, matching ingest
 *   - ``CASE:<folderName>`` for timeline tags, matched byte-exactly
 *   - ``<folderName>-<userId>`` for ``chat_key``
 *
 * ``selectedCase`` stays the display name alongside it: the RAG system prompt,
 * report titles, and the mobile-forensics intake lookup all want a human name.
 *
 * Returns ``null`` for All Cases (``*``) — there is no single case to scope to —
 * and ``null`` while the cases list is still loading or if the selection matches
 * no listed case. A ``null`` folder name makes assistant-service fall back to the
 * display name, i.e. today's behavior, so a cold cache degrades to the status quo
 * rather than to an unfiltered (cross-case) read.
 *
 * Both cases sources carry a server-owned ``caseTag`` and drop rows without one,
 * so this does not depend on the ``casesFromPermissionService`` rollout flag.
 * Never re-derive the folder name from the display name alone (TR-359).
 */
export function useCaseFolderName(caseId: string | null | undefined): string | null {
  const resolve = useCaseFolderNameResolver();
  return useMemo(() => resolve(caseId), [resolve, caseId]);
}

/**
 * Same resolution as {@link useCaseFolderName}, as a callback, for the many sites
 * that need the folder name of a case *other* than the currently selected one — a
 * case being switched to, the case a timeline build was started for, the case an
 * in-flight poll belongs to. Resolving against the live selection there would key
 * a request to whichever case the user happens to be looking at now.
 */
export function useCaseFolderNameResolver(): (caseId: string | null | undefined) => string | null {
  const casesQuery = useCasesList();
  const cases = casesQuery.data;

  return useCallback(
    (caseId: string | null | undefined) => {
      const trimmed = caseId?.trim() ?? '';
      if (!trimmed || isAllCasesSelection(trimmed)) return null;
      return resolveCaseFolderName(cases, trimmed);
    },
    [cases],
  );
}

/**
 * Await-able resolution of the same folder name, for the *write* side.
 *
 * {@link useCaseFolderName} and {@link useCaseFolderNameResolver} read whatever
 * the cases query has cached at render time and return ``null`` while it is still
 * loading. For a read that is only a graceful degradation: the server falls back
 * to the display name and the effect re-runs with the folder name once the list
 * lands. For a ``POST /chat`` it is not. Assistant-service composes ``chat_key``
 * from what the write was sent, so a send that raced the cases list writes the turn
 * to the display-name partition while every subsequent read — re-run with the
 * resolved folder name — looks in the UUID partition. The turn is then invisible
 * after a refresh, which is TR-359 again on a narrower window.
 *
 * So write paths await the list instead of sampling it. ``Main`` does the same
 * thing with its own ``resolveCaseFolderNameFor``; it cannot use this hook because
 * it renders the contexts these hooks read. Both go through
 * {@link casesListQueryOptions}, so they share one cache entry, and both apply the
 * ad-hoc list policy that ``useCasesList`` applies in ``select`` (which shapes the
 * view, not the cached data) — without it the synthetic My Files case resolves to
 * no folder name.
 *
 * Still returns ``null`` — never throws — when the session is not ready, the
 * selection is All Cases, or the fetch fails with nothing cached to fall back on,
 * so the caller degrades to the display name exactly as before.
 *
 * Honors the same preconditions ``useCasesList`` puts on ``enabled``, because
 * ``fetchQuery`` has no ``enabled``: firing anyway would send a request that
 * cannot succeed and cache the error under a key the nav abandons the moment
 * tenant context resolves.
 */
export function useAwaitedCaseFolderNameResolver(): (
  caseId: string | null | undefined,
) => Promise<string | null> {
  const queryClient = useQueryClient();
  const { cellId, accessToken } = useProvenanceSession();
  const fromPermissionService =
    useReactiveFeatureFlagValue('casesFromPermissionService') === true;
  const adHocEnabled = useReactiveFeatureFlagValue('adHoc') === true;
  const personalFilesCaseId = usePersonalFilesCaseId();
  const { idToken } = useReactiveAuthTokens();
  const tenantId = useMemo(() => getTenantIdFromToken(idToken), [idToken]);

  return useCallback(
    async (caseId: string | null | undefined) => {
      const trimmed = caseId?.trim() ?? '';
      if (!trimmed || isAllCasesSelection(trimmed)) return null;
      if (!cellId || !accessToken) return null;
      // The permission-service branch needs a tenant to resolve the caller's
      // cases; without one there is nothing to await, and asking would poison a
      // dead cache key with an error. Degrade rather than fetch.
      if (fromPermissionService && !tenantId) return null;

      const casesOptions = casesListQueryOptions({
        cellId,
        accessToken,
        fromPermissionService,
        tenantId,
      });
      let fetched: CaseSummary[] | undefined;
      try {
        fetched = await queryClient.fetchQuery(casesOptions);
      } catch {
        // A refresh of a stale entry rejects while the cache still holds the list
        // the nav is showing; resolving against those beats giving up.
        fetched = queryClient.getQueryData<CaseSummary[]>(casesOptions.queryKey);
      }
      return resolveCaseFolderName(
        fetched
          ? applyAdHocCaseListPolicy(fetched, adHocEnabled, personalFilesCaseId)
          : undefined,
        trimmed,
      );
    },
    [
      queryClient,
      cellId,
      accessToken,
      fromPermissionService,
      tenantId,
      adHocEnabled,
      personalFilesCaseId,
    ],
  );
}

/**
 * Display names for the tenant's ``folder_token`` case folders, keyed by token,
 * for relabelling paths in the evidence locker, breadcrumbs, scope chips and
 * Sources panel. See {@link caseFolderLabels}.
 *
 * Empty while the cases list is loading, which degrades to showing the raw path —
 * the same thing every one of these surfaces does today.
 */
export function useCaseFolderLabels(): Map<string, string> {
  const casesQuery = useCasesList();
  const cases = casesQuery.data;
  return useMemo(() => caseFolderLabels(cases), [cases]);
}

/**
 * Spreadable ``caseFolderName`` fragment for an assistant-service request body —
 * both the write side (``POST /chat``) and the ``/chat/history`` reads (POST list,
 * PUT session swap, DELETE purge, POST ``/archive``). One helper because the wire
 * field is the same name and the same value on every one of them.
 *
 * The two sides MUST agree: assistant-service composes the DynamoDB hash key as
 * ``{caseFolderName || caseId}-{userId}`` on reads (``_chat_key_from_body``) and as
 * ``{case_folder_name}-{userId}`` on writes (``ChatRequest.chat_key``), so a read
 * that omits this after a rename looks in a partition nothing was written to and
 * the user's history silently disappears (TR-359).
 *
 * Omits the key entirely when there is no resolved folder name, rather than sending
 * ``null``: the field is optional server-side and falls back to the display name,
 * which is correct for a legacy case (whose folder name IS its display name), and an
 * absent key documents "not resolved" more honestly than an explicit null, which
 * reads like a deliberate "no case".
 */
export function caseFolderNameBody(folderName: string | null): { caseFolderName?: string } {
  return folderName ? { caseFolderName: folderName } : {};
}

/**
 * The ``CASE:`` tag timeline-service stores on timelines and entity-extraction
 * jobs, built from the rename-safe folder name.
 *
 * Case-sensitive exact match on both sides (Postgres array overlap), and matched
 * against the caller's ``case:<folderName>`` access tags — so a display-name tag on
 * a UUID-folder case makes the timeline unreadable to the analyst who just
 * generated it, and a rename orphans it outright.
 *
 * Falls back to the selection itself, which is today's behavior and the right
 * answer for a legacy case (and keeps ``CASE:*`` for All Cases).
 */
export function caseTimelineTag(
  folderName: string | null,
  caseId: string | null | undefined,
): string {
  return `CASE:${folderName || (caseId ?? '').trim()}`;
}

/**
 * The assistant-service chat-history hash key, for the few places the frontend
 * has to predict it before a response comes back. Everywhere else, prefer the
 * ``chat_key`` the server returned.
 */
export function composeChatKey(
  folderName: string | null,
  caseId: string | null | undefined,
  userId: string | null | undefined,
): string {
  return `${folderName || (caseId ?? '').trim()}-${(userId ?? '').trim()}`;
}
